// ============================================
// INPUT VALIDATIONS & GUARDRAILS
// Comprehensive validation for user inputs and itinerary data
// ============================================

// ============================================
// DATE VALIDATIONS
// ============================================

/**
 * Validate trip dates
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @param {string} endDate - ISO date string (YYYY-MM-DD) - optional
 * @param {number} duration - Trip duration in days - optional
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export const validateTripDates = (startDate, endDate = null, duration = null) => {
  const errors = [];
  const warnings = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!startDate) {
    return { valid: true, errors: [], warnings: ['No start date specified'] };
  }

  const start = new Date(startDate + 'T00:00:00');

  // Check for valid date
  if (isNaN(start.getTime())) {
    errors.push('Invalid start date format');
    return { valid: false, errors, warnings };
  }

  // Check for past dates
  if (start < today) {
    errors.push('Start date cannot be in the past');
  }

  // Check for far future dates (more than 2 years)
  const twoYearsFromNow = new Date(today);
  twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
  if (start > twoYearsFromNow) {
    warnings.push('Trip is more than 2 years away - plans may change significantly');
  }

  // If end date provided, validate it
  if (endDate) {
    const end = new Date(endDate + 'T00:00:00');
    if (isNaN(end.getTime())) {
      errors.push('Invalid end date format');
    } else {
      if (end < start) {
        errors.push('End date must be after start date');
      }

      // Calculate duration
      const tripDuration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      if (tripDuration > 90) {
        warnings.push(`Trip duration of ${tripDuration} days is very long - consider breaking into multiple trips`);
      }
      if (tripDuration > 365) {
        errors.push('Trip duration cannot exceed 365 days');
      }
    }
  }

  // If duration provided, validate it
  if (duration !== null) {
    if (duration < 1) {
      errors.push('Trip must be at least 1 day');
    }
    if (duration > 90) {
      warnings.push(`Trip duration of ${duration} days is very long - consider breaking into multiple trips`);
    }
    if (duration > 365) {
      errors.push('Trip duration cannot exceed 365 days');
    }
  }

  return { valid: errors.length === 0, errors, warnings };
};

// ============================================
// INPUT SANITIZATION
// ============================================

/**
 * Sanitize text input to prevent XSS and limit length
 * @param {string} input - Raw user input
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} - Sanitized input
 */
export const sanitizeTextInput = (input, maxLength = 500) => {
  if (!input || typeof input !== 'string') return '';

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');

  // Escape special characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Trim whitespace and limit length
  sanitized = sanitized.trim().substring(0, maxLength);

  return sanitized;
};

/**
 * Validate destination name
 * @param {string} destination - Destination input
 * @returns {{ valid: boolean, sanitized: string, error: string|null }}
 */
export const validateDestination = (destination) => {
  if (!destination || typeof destination !== 'string') {
    return { valid: false, sanitized: '', error: 'Destination is required' };
  }

  const sanitized = sanitizeTextInput(destination, 100);

  if (sanitized.length < 2) {
    return { valid: false, sanitized, error: 'Destination name is too short' };
  }

  // Check for suspicious patterns (potential injection)
  const suspiciousPatterns = [
    /ignore.*previous/i,
    /forget.*instructions/i,
    /system.*prompt/i,
    /\beval\b/i,
    /\bexec\b/i,
    /javascript:/i,
    /data:/i,
    /<script/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(destination)) {
      return { valid: false, sanitized, error: 'Invalid destination name' };
    }
  }

  return { valid: true, sanitized, error: null };
};

// ============================================
// BUDGET VALIDATIONS
// ============================================

/**
 * Validate budget constraints
 * @param {number} budget - User's budget
 * @param {string} currency - Currency code
 * @param {number} duration - Trip duration in days
 * @returns {{ valid: boolean, errors: string[], warnings: string[], perDayBudget: number }}
 */
export const validateBudget = (budget, currency = 'USD', duration = 7) => {
  const errors = [];
  const warnings = [];

  if (!budget || budget <= 0) {
    return { valid: true, errors: [], warnings: ['No budget specified'], perDayBudget: 0 };
  }

  // Currency-adjusted minimum daily budgets
  const minDailyBudgets = {
    USD: 50,
    EUR: 45,
    GBP: 40,
    JPY: 5000,
    AUD: 70,
    CAD: 65,
    CHF: 80,
    DEFAULT: 50,
  };

  const minDaily = minDailyBudgets[currency] || minDailyBudgets.DEFAULT;
  const perDayBudget = budget / duration;

  if (perDayBudget < minDaily) {
    warnings.push(`Budget of ${perDayBudget.toFixed(0)} ${currency}/day is very tight. Consider increasing or shortening trip.`);
  }

  // Extremely low budget
  if (perDayBudget < minDaily / 2) {
    errors.push(`Budget of ${perDayBudget.toFixed(0)} ${currency}/day is unrealistically low for travel.`);
  }

  // Sanity check upper bound
  if (budget > 1000000) {
    warnings.push('Budget exceeds typical travel ranges - please verify amount');
  }

  return { valid: errors.length === 0, errors, warnings, perDayBudget };
};

// ============================================
// ACTIVITY TIMING VALIDATIONS
// ============================================

/**
 * Validate activity timing and feasibility
 * @param {Array} schedule - Array of activities for a day
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export const validateDaySchedule = (schedule) => {
  const errors = [];
  const warnings = [];

  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    return { valid: true, errors: [], warnings: [] };
  }

  // Parse time strings
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
    if (!match) return null;

    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]) || 0;
    const period = match[3];

    if (period) {
      if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
      if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    }

    return hours * 60 + minutes; // Return minutes from midnight
  };

  // Parse duration
  const parseDuration = (durationStr) => {
    if (!durationStr) return 60; // Default 1 hour
    const match = durationStr.match(/(\d+(?:\.\d+)?)\s*(hour|hr|h|minute|min|m)/i);
    if (!match) return 60;

    const value = parseFloat(match[1]);
    const unit = match[2].toLowerCase();

    if (unit.startsWith('h')) return value * 60;
    return value;
  };

  // Logistics items (checkout, drives, airport transfers) are sequentially dependent
  // and should not trigger overlap warnings against regular activities
  const isLogisticsItem = (name) => {
    if (!name) return false;
    const lower = name.toLowerCase();
    return /checkout|check.out|departure prep|drive (to|back|from)|road trip|airport transfer|head (to|back)|travel (to|back|from)|return (to|home|drive)/.test(lower);
  };

  let totalDuration = 0;
  let lastEndTime = 0;
  let lastWasLogistics = false;
  const sortedSchedule = [...schedule].sort((a, b) => {
    const timeA = parseTime(a.time) || 0;
    const timeB = parseTime(b.time) || 0;
    return timeA - timeB;
  });

  for (let i = 0; i < sortedSchedule.length; i++) {
    const activity = sortedSchedule[i];
    const startTime = parseTime(activity.time);
    const duration = parseDuration(activity.duration);
    const isLogistics = isLogisticsItem(activity.activity);

    if (startTime === null) continue;

    // Don't count logistics items (checkout, drives) toward total activity duration
    if (!isLogistics) {
      totalDuration += duration;
    }

    // Check for very early activities (skip logistics — early checkout/drive is normal)
    if (!isLogistics && startTime < 6 * 60) { // Before 6 AM
      warnings.push(`"${activity.activity}" starts very early (${activity.time})`);
    }

    // Check for very late activities
    if (startTime > 23 * 60) { // After 11 PM
      warnings.push(`"${activity.activity}" starts very late (${activity.time})`);
    }

    // Check for overlap with previous activity
    // Skip when either the current or previous item is logistics (checkout → drive is expected)
    if (i > 0 && startTime < lastEndTime && !isLogistics && !lastWasLogistics) {
      const overlapMinutes = lastEndTime - startTime;
      if (overlapMinutes > 15) { // Allow 15 min buffer
        warnings.push(`"${activity.activity}" may overlap with previous activity`);
      }
    }

    // Check for unrealistic activity duration (skip logistics — long drives are normal)
    if (!isLogistics && duration > 6 * 60) { // More than 6 hours
      warnings.push(`"${activity.activity}" has a very long duration (${activity.duration})`);
    }

    lastEndTime = startTime + duration;
    lastWasLogistics = isLogistics;
  }

  // Check total day duration
  if (totalDuration > 14 * 60) { // More than 14 hours of activities
    warnings.push('Day is very packed with activities - consider a more relaxed schedule');
  }

  if (totalDuration > 18 * 60) { // More than 18 hours
    errors.push('Day schedule exceeds realistic limits - activities total more than 18 hours');
  }

  return { valid: errors.length === 0, errors, warnings };
};

// ============================================
// MULTI-CITY VALIDATIONS
// ============================================

/**
 * Validate multi-city trip feasibility
 * @param {Array} stops - Array of stops with { name, days }
 * @param {number} totalDays - Total trip duration
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export const validateMultiCityTrip = (stops, totalDays) => {
  const errors = [];
  const warnings = [];

  if (!stops || stops.length < 2) {
    return { valid: true, errors: [], warnings: [] };
  }

  // Check minimum days per city
  const totalCityDays = stops.reduce((sum, stop) => sum + (stop.days || 1), 0);

  for (const stop of stops) {
    if ((stop.days || 1) < 1) {
      errors.push(`${stop.name} needs at least 1 day`);
    }
    // Only warn about short stays for longer trips with multiple stops
    // For short trips (≤3 days) or 2-stop trips, 1 day per city is expected
    if ((stop.days || 1) < 2 && totalDays > 3 && stops.length > 2) {
      warnings.push(`Consider spending at least 2 days in ${stop.name} to experience it properly`);
    }
  }

  // Check if days add up
  if (totalCityDays > totalDays) {
    errors.push(`City days (${totalCityDays}) exceed trip duration (${totalDays})`);
  }

  // Check for too many cities
  if (stops.length > 5) {
    warnings.push(`Visiting ${stops.length} cities may be rushed - consider fewer destinations`);
  }

  // Check days per city ratio (only for trips with 3+ stops where it's meaningful)
  const avgDaysPerCity = totalDays / stops.length;
  if (avgDaysPerCity < 2 && stops.length > 2) {
    warnings.push(`Average of ${avgDaysPerCity.toFixed(1)} days per city is very short - consider fewer destinations or longer trip`);
  }

  return { valid: errors.length === 0, errors, warnings };
};

// ============================================
// CONSTRAINT CONFLICT DETECTION
// ============================================

/**
 * Detect conflicting constraints in user preferences
 * @param {Object} preferences - User preferences object
 * @returns {{ hasConflicts: boolean, conflicts: string[] }}
 */
export const detectConstraintConflicts = (preferences) => {
  const conflicts = [];

  if (!preferences) return { hasConflicts: false, conflicts: [] };

  const {
    budget,
    travelStyle,
    accommodationType,
    mustHaves = [],
    interests = [],
    pace,
  } = preferences;

  // Budget vs Luxury conflicts
  if (budget === 'budget' || budget === 'low') {
    if (accommodationType === 'luxury' || accommodationType === '5-star') {
      conflicts.push('Budget travel preference conflicts with luxury accommodation choice');
    }
    if (mustHaves.some(m => /michelin|fine dining|luxury/i.test(m))) {
      conflicts.push('Budget preference conflicts with luxury dining requirements');
    }
  }

  // Pace vs Activities conflicts
  if (pace === 'relaxed' || pace === 'slow') {
    if (interests.length > 8) {
      conflicts.push('Relaxed pace preference conflicts with many interests - may not fit all activities');
    }
  }

  // Travel style conflicts
  if (travelStyle === 'adventure') {
    if (pace === 'relaxed') {
      conflicts.push('Adventure travel style may conflict with relaxed pace preference');
    }
  }

  // Duration vs destinations
  // (This would need destination count passed in)

  return { hasConflicts: conflicts.length > 0, conflicts };
};

// ============================================
// COMPREHENSIVE VALIDATION
// ============================================

/**
 * Run all validations on trip parameters
 * @param {Object} tripParams - All trip parameters
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export const validateTripParameters = (tripParams) => {
  const allErrors = [];
  const allWarnings = [];

  const {
    destination,
    startDate,
    endDate,
    duration,
    budget,
    currency,
    preferences,
    stops,
    schedule,
  } = tripParams;

  // Validate destination
  if (destination) {
    const destResult = validateDestination(destination);
    if (!destResult.valid) {
      allErrors.push(destResult.error);
    }
  }

  // Validate dates
  const dateResult = validateTripDates(startDate, endDate, duration);
  allErrors.push(...dateResult.errors);
  allWarnings.push(...dateResult.warnings);

  // Validate budget
  if (budget) {
    const budgetResult = validateBudget(budget, currency, duration || 7);
    allErrors.push(...budgetResult.errors);
    allWarnings.push(...budgetResult.warnings);
  }

  // Validate multi-city
  if (stops && stops.length > 1) {
    const multiCityResult = validateMultiCityTrip(stops, duration || 7);
    allErrors.push(...multiCityResult.errors);
    allWarnings.push(...multiCityResult.warnings);
  }

  // Validate schedule
  if (schedule) {
    const scheduleResult = validateDaySchedule(schedule);
    allErrors.push(...scheduleResult.errors);
    allWarnings.push(...scheduleResult.warnings);
  }

  // Detect conflicts
  if (preferences) {
    const conflictResult = detectConstraintConflicts(preferences);
    allWarnings.push(...conflictResult.conflicts);
  }

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

export default {
  validateTripDates,
  sanitizeTextInput,
  validateDestination,
  validateBudget,
  validateDaySchedule,
  validateMultiCityTrip,
  detectConstraintConflicts,
  validateTripParameters,
};
